import React, { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Alert, Box, Button, Stack, TablePagination } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { nlsAnalyticsQueryKeys } from '~/hooks/apis/nls-analytics/nlsAnalyticsQueryKeys';
import { useCategory2Queries } from '~/hooks/apis/nls-analytics/useCategory2Queries';

import { Category2QueryRow } from '~/services/NlsAnalyticsService';

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const GRID_HEIGHT_PX = 520;

type Category2GridRow = Category2QueryRow & { id: string };

const columns: Array<GridColDef<Category2GridRow>> = [
  {
    field: 'queryText',
    headerName: 'Query',
    flex: 2,
    minWidth: 280,
    sortable: false,
    display: 'flex',
  },
  {
    field: 'spaceCount',
    headerName: 'Spaces',
    width: 90,
    sortable: false,
    display: 'flex',
  },
  {
    field: 'searchCount',
    headerName: 'Searches (30d)',
    width: 130,
    sortable: false,
    display: 'flex',
  },
  {
    field: 'uniqueSearcherCount',
    headerName: 'Unique searchers',
    width: 140,
    sortable: false,
    display: 'flex',
  },
  {
    field: 'matchedName',
    headerName: 'Matched name',
    flex: 1,
    minWidth: 200,
    sortable: false,
    display: 'flex',
    valueGetter: (_value, row) => row.matchedName || '—',
  },
];

export default function Category2QueriesTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const offset = page * pageSize;

  const { data, isLoading, isFetching, error, refetch } = useCategory2Queries({
    limit: pageSize,
    offset,
  });

  const rows = useMemo<Array<Category2GridRow>>(
    () =>
      (data ?? []).map((row, index) => ({
        ...row,
        id: `${offset + index}-${row.queryText}-${row.spaceCount}-${row.searchCount}-${row.uniqueSearcherCount}`,
      })),
    [data, offset],
  );

  const loading = isLoading || isFetching;

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: nlsAnalyticsQueryKeys.all });
    void refetch();
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Category 2 queries are hotel natural-language searches from the last 30 days with at most 6 spaces that do not
        exactly match a property, landmark, destination, offer, or airport name from typeahead, resolve as a
        property/offer search, or match a known property name with a location suffix. Excludes &quot;Map area&quot; and
        AU place + state abbrev queries (e.g. Gold Coast, QLD).
      </Alert>

      {error && <Alert severity="error">Failed to load Category 2 queries. Please try again.</Alert>}

      <Box>
        <Button onClick={handleRefresh} variant="contained" disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Box sx={{ height: GRID_HEIGHT_PX, width: '100%' }}>
        <DataGrid
          loading={loading}
          columns={columns}
          rows={rows}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter
          slots={{
            noRowsOverlay: () => (
              <Stack alignItems="center" justifyContent="center" height="100%" spacing={1}>
                <Box>{loading ? 'Loading…' : 'No Category 2 queries found'}</Box>
              </Stack>
            ),
          }}
        />
      </Box>

      <TablePagination
        component="div"
        count={-1}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(Number.parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
        labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        labelRowsPerPage="Rows per page:"
        nextIconButtonProps={{ disabled: loading || rows.length < pageSize }}
        backIconButtonProps={{ disabled: loading || page === 0 }}
      />
    </Stack>
  );
}
